
import { Dialog, DialogContent, IconButton, InputAdornment, MenuItem, TextField } from "@mui/material"
import { BoxFC, BoxFR } from "components/BoxCustom"
import CheckboxFormControl from "components/CheckboxFormControl"
import PaperComponent from "components/PaperComponent"
import { DialogFooter, DialogHeader } from "components/dialog/DialogHeader"
import { GlobalStateContext } from "contexts/GlobalStateContext"
import React, { useCallback, useContext, useEffect, useState } from "react"
import { initSparePartDialogData } from "./initData"
import { sparePartApiNm, sparePartColNm } from "./constant"
import { alertConfirmDelete } from "components/Alert"
import { bahtProps } from "utils/bahtProps"
import { AutoAwesomeRounded } from "resource/muiImport"
import FilePreviewCard from "components/FileUplaod/FilePreviewCard"

const SparePartDialog = ({ selectedId, dialogOpen, setDialogOpen, onFinish, lastFilter, sparePartTypes }) => {

  const { ax } = useContext(GlobalStateContext)
  const [dialogData, setDialogData] = useState({ ...initSparePartDialogData })
  const [origData, setOrigData] = useState({ ...initSparePartDialogData })
  const [filesData, setFilesData] = useState([])
  const [pendingFiles, setPendingFiles] = useState([])

  const dialogDataProp = useCallback((name) => ({
    fullWidth: true,
    size: "small",
    label: sparePartColNm[name],
    value: dialogData[name] ?? "",
    onChange: (e) => setDialogData(o => ({ ...o, [name]: e.target.value })),
  }), [dialogData])

  const uploadFiles = useCallback(async (spId) => {
    for (const pf of pendingFiles) {
      const formData = new FormData()
      formData.append('file', pf.file)
      formData.append('SpId', spId)
      formData.append('Label', pf.name)
      await ax.post(sparePartApiNm.uploadSparePartFile, formData)
    }
    setPendingFiles([])
  }, [ax, pendingFiles])

  const handleDialogOk = useCallback(async () => {
    if (selectedId) {
      // Update: send only changed columns
      const colValues = {}
      for (const key of ['IsActv', 'SpTypId', 'SpCd', 'SpNm', 'SpSNm', 'Brand', 'Model', 'Unt', 'UntPr', 'MinStk', 'CurStk', 'Dscp', 'Rmk']) {
        if (String(dialogData[key] ?? '') !== String(origData[key] ?? '')) {
          colValues[key] = dialogData[key]
        }
      }
      const hasChange = Object.keys(colValues).length > 0
      const hasNewFiles = pendingFiles.length > 0
      if (hasChange) {
        await ax.post(sparePartApiNm.updateSparePartColumn, {
          SpId: selectedId,
          ColValues: colValues,
          getArgs: lastFilter,
        })
      }
      if (hasNewFiles) {
        await uploadFiles(selectedId)
      }
      if (hasChange || hasNewFiles) {
        onFinish()
      }
      setDialogOpen(false)
    } else {
      // Insert - don't pass getArgs so we get insertId back
      const postData = { ...dialogData }
      const value = await ax.post(sparePartApiNm.insertSparePart, postData)
      if (value.data) {
        const spId = value.data
        if (spId && pendingFiles.length > 0) {
          await uploadFiles(spId)
        }
        onFinish()
        setDialogOpen(false)
      }
    }
  }, [ax, dialogData, origData, lastFilter, onFinish, setDialogOpen, selectedId, pendingFiles, uploadFiles])

  const handleGenerateCode = useCallback(() => {
    ax.post("/branch/generateSparePartCode", { SpCd: dialogData.SpCd || null }).then(value => {
      if (value.data) {
        setDialogData(o => ({ ...o, SpCd: value.data.SpCd }))
      }
    })
  }, [ax, dialogData.SpCd])

  const handleDelete = useCallback(() => {
    alertConfirmDelete(() => {
      ax.post(sparePartApiNm.deleteSparePart, { SpId: selectedId, getArgs: lastFilter }).then(value => {
        if (value.data) {
          onFinish(value.data)
          setDialogOpen(false)
        }
      })
    })
  }, [ax, onFinish, lastFilter, selectedId, setDialogOpen])

  const handleDeleteServerFile = useCallback((file) => {
    return new Promise((resolve) => {
      alertConfirmDelete(() => {
        ax.post(sparePartApiNm.deleteSparePartFile, { SpFileId: file.SpFileId }).then(value => {
          if (value.data) {
            resolve()
          }
        })
      })
    })
  }, [ax])

  useEffect(() => {
    if (dialogOpen) {
      if (selectedId) {
        ax.post(sparePartApiNm.getSparePartDetail, { SpId: selectedId }).then(value => {
          if (value.data && value.data.length > 0) {
            setDialogData(value.data[0])
            setOrigData(value.data[0])
            setFilesData(value.data[0].Files || [])
          }
        })
      } else {
        setFilesData([])
      }
    } else {
      setDialogData({ ...initSparePartDialogData })
      setOrigData({ ...initSparePartDialogData })
      setFilesData([])
      setPendingFiles(o => {
        o.forEach(f => URL.revokeObjectURL(f.preview))
        return []
      })
    }
  }, [dialogOpen, ax, selectedId])

  return (
    <Dialog
      open={dialogOpen}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
      fullWidth maxWidth='xs'
    >
      <DialogHeader handleDialogClose={() => setDialogOpen(false)}
        title="ข้อมูลอะไหล่"
        onDeleteClick={selectedId ? handleDelete : null}
      />
      <DialogContent>
        <BoxFC pt={2} sx={{ gap: 1 }}>
          <CheckboxFormControl label={"Active"}
            checked={dialogData.IsActv === 1}
            onChange={e => setDialogData(o => ({ ...o, IsActv: e.target.checked ? 1 : 0 }))} />
          <TextField {...dialogDataProp('SpCd')} InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleGenerateCode}>
                  <AutoAwesomeRounded color="primary" />
                </IconButton>
              </InputAdornment>
            )
          }} />
          <TextField {...dialogDataProp('SpNm')} required />
          <TextField {...dialogDataProp('SpSNm')} />
          <TextField {...dialogDataProp('SpTypId')} select>
            <MenuItem value="">ไม่ระบุ</MenuItem>
            {sparePartTypes.map(t => (
              <MenuItem key={t.SpTypId} value={t.SpTypId}>{t.SpTypNm}</MenuItem>
            ))}
          </TextField>
          <BoxFR sx={{ gap: 1 }}>
            <TextField {...dialogDataProp('Brand')} />
            <TextField {...dialogDataProp('Model')} />
          </BoxFR>
          <BoxFR sx={{ gap: 1 }}>
            <TextField {...dialogDataProp('Unt')}  />
            <TextField {...dialogDataProp('UntPr')} type="number" {...bahtProps} />
          </BoxFR>
          <BoxFR sx={{ gap: 1 }}>
            <TextField {...dialogDataProp('MinStk')} type="number" />
            <TextField {...dialogDataProp('CurStk')} type="number" disabled={!!selectedId} />
          </BoxFR>
          <TextField {...dialogDataProp('Dscp')} multiline rows={2} />
          <TextField {...dialogDataProp('Rmk')} />
          <FilePreviewCard
            filesData={filesData}
            setFilesData={setFilesData}
            pendingFiles={pendingFiles}
            setPendingFiles={setPendingFiles}
            onDeleteServerFile={handleDeleteServerFile}
            fileIdKey="SpFileId"
          />
        </BoxFC>
      </DialogContent>
      <DialogFooter okText="บันทึก"
        handleDialogClose={() => setDialogOpen(false)}
        handleDialogOk={handleDialogOk} />
    </Dialog>
  )
}

export default React.memo(SparePartDialog)
